/**

 * Icons under `public/assets/technology-research/*.webp` — matched from card title / description text.

 */



export const TECH_RESEARCH_ASSET_BASE = 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/technology-research'

/** Center badge on completed tech cards (`public/assets/technology-research/completed.webp`). */
export const TECH_RESEARCH_COMPLETED_BADGE_URL = `${TECH_RESEARCH_ASSET_BASE}/completed.webp`

/** Locked tech cards — centered on icon (`public/assets/technology-research/lock_icon.webp`). */
export const TECH_RESEARCH_LOCK_ICON_URL = `${TECH_RESEARCH_ASSET_BASE}/lock_icon.webp`

export const TECH_RESEARCH_DEFAULT_ICON = 'order.webp'



/** ECS-equivalent per kingdom-tree node by era (display / parity with server). */
export const KINGDOM_TREE_RESEARCH_TARGET_ECS_BY_ERA = {
  SURVIVAL: 10,
  SETTLER: 20,
  KINGDOM: 30,
  HIGH_KINGDOM: 40,
  IMPERIAL: 50
} as const

/** Resource amounts equal in value to 1 ECS (bread/wood/stone/iron only). */
export const ECS_VALUE_IN_RESOURCES = {
  bread: 80,
  wood: 80,
  stone: 40,
  iron: 20
} as const

/** Every kingdom-tree research cost must allocate more than this amount per resource. */
export const KINGDOM_TREE_RESEARCH_MIN_RESOURCE_AMOUNT = 21

export type KingdomTreeResearchEra = keyof typeof KINGDOM_TREE_RESEARCH_TARGET_ECS_BY_ERA

export type KingdomTreeResourceCost = {
  bread: number
  wood: number
  stone: number
  iron: number
}

function targetEcsForEra(era: KingdomTreeResearchEra): number {
  return KINGDOM_TREE_RESEARCH_TARGET_ECS_BY_ERA[era] ?? 10
}

function kingdomTreeResearchCostSeed(era: string, slotIndex: number): number {
  const key = `${era}_${slotIndex}`
  let seed = 0
  for (let i = 0; i < key.length; i++) {
    seed = (seed * 31 + key.charCodeAt(i)) >>> 0
  }
  return seed
}

/** Deterministic resource cost for a kingdom-tree slot (matches server). */
export function generateKingdomTreeResearchCost(
  era: KingdomTreeResearchEra,
  slotIndex: number
): KingdomTreeResourceCost {
  const targetEcs = targetEcsForEra(era)
  const P = Math.round(targetEcs * 80)
  const min = KINGDOM_TREE_RESEARCH_MIN_RESOURCE_AMOUNT
  if (!Number.isFinite(P) || P <= 0) {
    return { bread: 0, wood: 0, stone: 0, iron: 0 }
  }

  const floorValue = min + min + 2 * min + 4 * min
  let remainder = P - floorValue
  if (remainder < 0) {
    return { bread: min, wood: min, stone: min, iron: min }
  }

  const seed = kingdomTreeResearchCostSeed(era, slotIndex)
  const maxExtraIron = Math.floor(remainder / 4)
  const extraIron = maxExtraIron > 0 ? seed % (maxExtraIron + 1) : 0
  remainder -= 4 * extraIron
  const maxExtraStone = Math.floor(remainder / 2)
  const extraStone = maxExtraStone > 0 ? Math.floor(seed / 7) % (maxExtraStone + 1) : 0
  remainder -= 2 * extraStone
  const extraWood = remainder > 0 ? Math.floor(seed / 49) % (remainder + 1) : 0
  const extraBread = remainder - extraWood

  return {
    bread: min + extraBread,
    wood: min + extraWood,
    stone: min + extraStone,
    iron: min + extraIron
  }
}

/** Tooltip when a tree belongs to an era the empire has not reached yet */
export const EMPIRE_ERA_LOCK_HINT =
  'Locked until your empire advances to this era. Complete all technologies in your current era, then advance your empire to unlock research here.'



/**

 * First matching rule wins. More specific patterns before broad ones.

 */

const TECH_ICON_RULES: ReadonlyArray<{ re: RegExp; file: string }> = [

  { re: /enemy hero execution/i, file: 'kill_hero.webp' },

  { re: /captured general conversion/i, file: 'turn_hero.webp' },

  { re: /unlimited storage/i, file: 'storage_expansion.webp' },

  { re: /item usage in battle/i, file: 'use_weapon.webp' },

  { re: /mvm hero limit|hero limitation/i, file: 'hero_limitation.webp' },

  { re: /marriage alliance/i, file: 'alliance_marriage.webp' },

  { re: /attack supply refill/i, file: 'supplies_replenishment.webp' },

  { re: /alliance direct aid/i, file: 'direct_aid.webp' },

  { re: /dictator punishment/i, file: 'dictator_punishment.webp' },

  { re: /withdraw function/i, file: 'unlock_withdraw.webp' },

  { re: /counter espionage/i, file: 'counter_espionage.webp' },

  { re: /royalty decrease/i, file: 'decrease_royalty.webp' },

  { re: /equipment purchase/i, file: 'equipment_acquisition.webp' },

  { re: /hero purchase/i, file: 'hero_acquisition.webp' },

  { re: /equipment selling/i, file: 'sell_equipment.webp' },

  { re: /hero selling/i, file: 'sell_hero.webp' },

  { re: /storage expanded/i, file: 'storage_expansion.webp' },

  { re: /ceasefire tribute/i, file: 'ceasefire_tribute.webp' },

  { re: /alliance tribute/i, file: 'alliance_tribute.webp' },

  { re: /repair cost/i, file: 'repair_building.webp' },

  { re: /upgrade cost/i, file: 'upgrade_building.webp' },

  { re: /training ground/i, file: 'training_ground.webp' },

  { re: /marketplace unlocked|^marketplace$/i, file: 'unlock_marketplace.webp' },

  { re: /resource stealing/i, file: 'resource_stealing.webp' },

  { re: /training time/i, file: 'training_speed.webp' },

  { re: /castle defense|defense \+/i, file: 'castle_defense.webp' },

  {

    re: /\d+\s*-?\s*minute orders unlocked|\d+\s*-?\s*hour orders unlocked|1h\s*\/\s*4h orders/i,

    file: 'order.webp'

  },

  { re: /form alliances with other users through marriage|marriage/i, file: 'alliance_marriage.webp' },

  // `user_weapon.webp` was a typo — that file 404s on the CDN. The real icon is `use_weapon.webp`,
  // which the `item usage in battle` rule above already uses.
  { re: /unlock character can use item|use item for the battle/i, file: 'use_weapon.webp' },

  { re: /replenish supply/i, file: 'supplies_replenishment.webp' },

  { re: /decrease royalty/i, file: 'decrease_royalty.webp' },

  { re: /direct aid/i, file: 'direct_aid.webp' },

  { re: /obtain equipment using game points/i, file: 'equipment_acquisition.webp' },

  { re: /obtain heroes using game points/i, file: 'hero_acquisition.webp' },

  { re: /sell equipment/i, file: 'sell_equipment.webp' },

  { re: /sell heroes/i, file: 'sell_hero.webp' },

  { re: /kill hero/i, file: 'kill_hero.webp' },

  { re: /captured general|turn a captured/i, file: 'turn_hero.webp' },

  { re: /marketplace/i, file: 'unlock_marketplace.webp' },

  { re: /ceasefire request/i, file: 'ceasefire_tribute.webp' },

  { re: /alliance demands/i, file: 'alliance_tribute.webp' },

  { re: /resource storage capacity expansion|storage capacity expansion/i, file: 'storage_expansion.webp' },

  { re: /building repair/i, file: 'repair_building.webp' },

  { re: /building upgrade/i, file: 'upgrade_building.webp' },

  { re: /reducing training time|training time by/i, file: 'training_speed.webp' },

  { re: /castle.*defense|defense when attacking other castles/i, file: 'castle_defense.webp' },

  { re: /\d+\s*m\b.*order|\d+\s*h\b.*order|unlock\s+\d|unlock.*\border\b/i, file: 'order.webp' },

  { re: /steal resources/i, file: 'resource_stealing.webp' }

]



export function getTechnologyResearchIconUrl(description: string): string {

  const s = description.trim()

  if (!s) {

    return `${TECH_RESEARCH_ASSET_BASE}/${TECH_RESEARCH_DEFAULT_ICON}`

  }

  for (const { re, file } of TECH_ICON_RULES) {

    if (re.test(s)) {

      return `${TECH_RESEARCH_ASSET_BASE}/${file}`

    }

  }

  return `${TECH_RESEARCH_ASSET_BASE}/${TECH_RESEARCH_DEFAULT_ICON}`

}

/** True when the resolved icon is `storage_expansion.webp` (long titles — slightly smaller type in UI). */

export function isStorageExpansionTechnology(description: string): boolean {

  return getTechnologyResearchIconUrl(description).endsWith('/storage_expansion.webp')

}

/** Paired backgrounds: `card_icon_bg_{n}.webp` + `card_body_bg_{n}.webp` under `technology-research/`. */
export type TechnologyResearchCardBgVariant = 1 | 2 | 3 | 4 | 5

/** First matching group wins: commerce → storage/buildings → training/espionage → diplomacy → combat/MvM. */
const TECH_CARD_BG_VARIANT_RULES: ReadonlyArray<{
  variant: TechnologyResearchCardBgVariant
  res: readonly RegExp[]
}> = [
  {
    variant: 5,
    res: [
      /obtain equipment using game points|equipment purchase/i,
      /obtain heroes using game points|hero purchase/i,
      /withdraw function/i,
      /marketplace/i,
      /sell equipment|equipment selling/i,
      /sell heroes|hero selling/i,
      /\b10m\b.*order|\b20m\b.*order|\b8h\b.*order|\b24h\b.*order/i,
      /1h\s*[,/&]?\s*4h.*order/i,
      /\d+\s*-?\s*minute orders unlocked|\d+\s*-?\s*hour orders unlocked/i,
      /unlock\s+.*\d+m\s+order/i,
      /unlock\s+.*\d+h.*order/i,
    ],
  },
  {
    variant: 4,
    res: [
      /resource storage capacity|storage capacity expansion|unlimited storage|storage expanded/i,
      /wheat.*bread|bread.*others|no limit/i,
      /reduce building upgrade|building upgrade.*(time|resource|consumption)/i,
      /reduce building repair|building repair.*(time|resource|consumption)/i,
      /upgrade cost.*time|repair cost.*time/i,
    ],
  },
  {
    variant: 3,
    res: [
      /training ground/i,
      /training time|reducing training time/i,
      /counter espionage/i,
      /steal resources|resource stealing/i,
      /decrease royalty|royalty decrease/i,
      /kill hero|enemy hero execution/i,
      /captured general|turn a captured|general conversion/i,
    ],
  },
  {
    variant: 2,
    res: [
      /ceasefire.*tribute|tribute.*ceasefire|ceasefire request/i,
      /marriage alliance|form alliances.*marriage|alliances through marriage/i,
      /dictator punishment/i,
      /direct aid/i,
      /alliance demands.*tribute|tribute.*alliance demands/i,
    ],
  },
  {
    variant: 1,
    res: [
      /unlock character can use item|use item.*battle|item usage in battle/i,
      /hero limitation|mvm.*hero limit|hero limit.*mvm/i,
      /replenish supply|supply refill|replenish supply function|attack supply/i,
      /castle.*defense|defense when attacking|defense.*attacking.*castle/i,
      /defense\s*\+\s*\d+%/i,
    ],
  },
]

export function getTechnologyResearchCardBgVariant(description: string): TechnologyResearchCardBgVariant {
  const s = description.trim()
  if (!s) return 1
  for (const { variant, res } of TECH_CARD_BG_VARIANT_RULES) {
    if (res.some(re => re.test(s))) return variant
  }
  return 1
}

export function getTechnologyResearchCardBackgroundUrls(description: string): {
  variant: TechnologyResearchCardBgVariant
  iconBg: string
  bodyBg: string
} {
  const variant = getTechnologyResearchCardBgVariant(description)
  return {
    variant,
    iconBg: `${TECH_RESEARCH_ASSET_BASE}/card_icon_bg_${variant}.webp`,
    bodyBg: `${TECH_RESEARCH_ASSET_BASE}/card_body_bg_${variant}.webp`,
  }
}

/**
 * Page chrome drawn by `technology-research-page.tsx`, which builds these from its own local
 * `TR` base rather than importing them one by one. Kept here so the preload manifest has a single
 * place to read the page's artwork from — if you add a `${TR}/…` image to that page, add its
 * filename here too or it will not be gated.
 */
const TECH_RESEARCH_PAGE_CHROME_FILES: readonly string[] = [
  'bottom_bar_bg.webp',
  'era_details_bg.webp',
  'high_kingdom_bottom.webp',
  'high_kingdom_era.webp',
  'imperial_bottom.webp',
  'imperial_era.webp',
  'kingdom_bottom.webp',
  'kingdom_era.webp',
  'settler_bottom.webp',
  'settler_era.webp',
  'survival_bottom.webp',
  'survival_era.webp',
  'top_bar_bg.webp',
  'view_bg.webp',
]

/**
 * Every `technology-research/` image the research page can show, for the startup gate.
 *
 * The icons and card backgrounds are picked at runtime by matching a technology's description
 * text, so nothing in the source is a literal URL and `npm run generate:land-assets` cannot see
 * any of it. Deriving the set from the same rule tables the page uses keeps the two in step.
 */
export const TECHNOLOGY_RESEARCH_PRELOAD_URLS: readonly string[] = [
  ...new Set<string>([
    TECH_RESEARCH_COMPLETED_BADGE_URL,
    TECH_RESEARCH_LOCK_ICON_URL,
    `${TECH_RESEARCH_ASSET_BASE}/${TECH_RESEARCH_DEFAULT_ICON}`,
    ...TECH_ICON_RULES.map(({ file }) => `${TECH_RESEARCH_ASSET_BASE}/${file}`),
    ...TECH_CARD_BG_VARIANT_RULES.flatMap(({ variant }) => [
      `${TECH_RESEARCH_ASSET_BASE}/card_icon_bg_${variant}.webp`,
      `${TECH_RESEARCH_ASSET_BASE}/card_body_bg_${variant}.webp`,
    ]),
    ...TECH_RESEARCH_PAGE_CHROME_FILES.map((f) => `${TECH_RESEARCH_ASSET_BASE}/${f}`),
  ]),
]


